/**
 * MatRenderer — the cutting-mat background + brand render cluster, extracted
 * from Scene (#852, epic #828). Scene OWNS a MatRenderer instance and DRIVES
 * it by composition (it is NOT a base class), mirroring the ConstructionTool
 * controller pattern (`SelectEditController` / `DrawSubModeController` etc.):
 * the renderer exclusively owns the mat-render STATE (the cached drawn-mat
 * group keyed by look-signature, the per-part / per-line content-bbox caches,
 * and the sticky resolved mat layout), while everything shared it needs from
 * the owning Scene (the paper scope + root layer, the active mat units, and
 * the grid-step sizes that stay single-definition on Scene because non-mat
 * code — `nudgeStep`, `getMatGridInfo`, the drag-guide formatter — reads
 * them too) is injected through the `MatRenderHost` interface.
 *
 * Scene calls `renderMat(project)` from `render()` (preserving the exact
 * bottom-of-stack draw order), and delegates its public mat queries
 * (`getMatLayout` / `getMatGridInfo`) plus the unit-switch / project-replace
 * cache lifecycle to the renderer. Behaviour is identical to the in-Scene
 * original — this is a pure move + host rewiring.
 */
import paper from "paper";
import {
  projectConstructionLines,
  projectTracingImages,
  tracingImageBounds,
  type ConstructionLine,
  type MatLayout,
  type Piece,
  type Project,
} from "../model/Project";
import { cssVar } from "../colors";
import { getPxPerMm } from "../calibration";

// Cutting-mat palette. Defined as CSS vars `--mat-bg`, `--mat-grid-*` —
// the values match the dark forest tone of Olfa / Fiskars / OttLite mats
// with green-grey grid lines.
const MAT_GREEN = () => cssVar("--mat-bg");
const MAT_GRID_MINOR = () => cssVar("--mat-grid-fine"); // every 10 mm
export const MAT_GRID_MAJOR = () => cssVar("--mat-grid-coarse"); // every 50 mm
const MAT_GRID_AXIS = () => cssVar("--mat-axis"); // cross axes through the mat center
/** Outer corner radius (mm) of the mat. */
const MAT_CORNER_RADIUS = 12;
/** Padding (mm) between the mat's outer rounded edge and the inner border
 *  line — same on all sides. 1/2 inch = 12.7 mm matches the typical
 *  margin printed on real cutting mats. */
export const MAT_PADDING = 12.7;
/** Width of the grid frame's line (the inner border), in screen pixels. */
export const MAT_BORDER_PX = 0.8;
/** Thickness of the delayed-load progress bar (#1230), in screen pixels. */
export const LOAD_BAR_PX = 4;

/** Standard cutting-mat sizes, stored portrait-first in mm and sorted
 *  smallest → largest by area. The picker walks the active list for the
 *  smallest size that envelops the parts' bounding box; mats are always
 *  rendered landscape so the long side becomes the width. The metric list
 *  follows the ISO A-series; the imperial list covers the popular hobby
 *  and pro mats sold by Olfa / Fiskars / Alvin / etc. */
const STANDARD_MAT_SIZES_MM_METRIC: ReadonlyArray<{
  w: number;
  h: number;
  name: string;
}> = [
  { w: 148, h: 210, name: "A5" },
  { w: 210, h: 297, name: "A4" },
  { w: 297, h: 420, name: "A3" },
  { w: 420, h: 594, name: "A2" },
  { w: 594, h: 841, name: "A1" },
  { w: 841, h: 1189, name: "A0" },
];
const STANDARD_MAT_SIZES_MM_IMPERIAL: ReadonlyArray<{ w: number; h: number }> =
  [
    { w: 88.9, h: 139.7 }, // 3.5" × 5.5"
    { w: 139.7, h: 215.9 }, // 5.5" × 8.5"
    { w: 215.9, h: 304.8 }, // 8.5" × 12"
    { w: 304.8, h: 457.2 }, // 12" × 18"
    { w: 457.2, h: 609.6 }, // 18" × 24"
    { w: 457.2, h: 914.4 }, // 18" × 36"
    { w: 609.6, h: 914.4 }, // 24" × 36"
    { w: 762, h: 1066.8 }, // 30" × 42"
    { w: 914.4, h: 1219.2 }, // 36" × 48"
    { w: 914.4, h: 1828.8 }, // 36" × 72"
    { w: 1219.2, h: 2438.4 }, // 48" × 96"
  ];
/** Project-mm → CSS-px factor at 1:1 zoom is owned by ../calibration so a
 *  user-applied per-display calibration multiplier propagates to every
 *  caller (mat-size picker, viewport zoom, etc.). */

type BBox = { minX: number; minY: number; maxX: number; maxY: number };

/** The shared Scene state + services the mat renderer reaches back into.
 *  The paper scope + root layer stay owned by Scene (the whole layer stack
 *  is shared with the piece / construction / stitch renderers); the active
 *  units + grid steps stay single-definition on Scene because non-mat code
 *  reads them too. Implemented by Scene. */
export interface MatRenderHost {
  getPaperScope(): paper.PaperScope;
  getRootLayer(): paper.Layer;
  getMatUnits(): "mm" | "in";
  gridSteps(): { minor: number; major: number; extra: number };
}

export class MatRenderer {
  /** Cached content bbox per piece / construction line for `computeMatLayout`
   *  (#579). The mat envelope (and the brand-corner overlap pre-filter) only
   *  need each part's bbox, which is a pure function of its (immutable) path
   *  data — so keying by object identity reuses the parse across renders.
   *  Without this the mat re-parsed every part on every render (~77% of render
   *  cost at scale). The full path is built lazily and only for the rare part
   *  grazing the brand corner. */
  private matPieceBboxCache = new Map<string, { piece: Piece; bbox: BBox }>();
  private matLineBboxCache = new Map<
    string,
    { line: ConstructionLine; bbox: BBox }
  >();
  /** The drawn mat (grid lines, ticks, border, tagline) wrapped in one group,
   *  cached by a signature of everything that affects its look — layout, units,
   *  theme colors (#579). The grid/ticks are many segments to draw, but depend
   *  only on the mat, not the parts, so this is rebuilt only when the mat
   *  actually changes ("drawn once on resize") instead of every render. */
  private matRenderCache: { sig: string; group: paper.Item } | null = null;
  /** Cached mat layout — sticks once it's been established around the
   *  parts so user-driven moves don't shove the mat around the canvas.
   *  Cleared on unit switch; on project replacement (open / reset) it is
   *  re-seeded from the incoming project's persisted `matLayout` (#785
   *  follow-up) — or null when it has none, so the mat re-snaps to the new
   *  design's bounding box. */
  private cachedMatLayout: MatLayout | null = null;
  /** The mat the saved project will bring, laid out while it loads (#1230)
   *  so the mat doesn't shift when the project arrives. Used only for the
   *  empty project shown during the load; null otherwise. */
  private loadingLayout: MatLayout | null = null;
  /** While the delayed-load progress bar shows (#1230), the mat's background
   *  leaves a LOAD_BAR_PX strip open just below the grid's top line, so the
   *  bar, a DOM element behind the canvas, shows through it beneath the grid
   *  lines and ticks. */
  private loadingStrip = false;

  constructor(private host: MatRenderHost) {}

  /** The paper scope + root layer are owned by Scene and reached through the
   *  host; the mat renderer draws onto the same shared layer stack. */
  private get scope(): paper.PaperScope {
    return this.host.getPaperScope();
  }
  private get rootLayer(): paper.Layer {
    return this.host.getRootLayer();
  }

  /** The mat placement resolved by the last render, copied so callers can't
   *  mutate the live cache. Backs Scene's public `getMatLayout()`. */
  getMatLayout(): MatLayout | null {
    return this.cachedMatLayout ? { ...this.cachedMatLayout } : null;
  }

  /** The live cached layout (not a copy) — for Scene's `getMatGridInfo`,
   *  which reads it exactly as the in-Scene original did before falling
   *  back to a fresh `computeMatLayout`. */
  peekCachedMatLayout(): MatLayout | null {
    return this.cachedMatLayout;
  }

  /** Open or close the progress bar's strip in the mat. */
  setLoadingStrip(open: boolean): void {
    this.loadingStrip = open;
  }

  /** Lay the empty project's mat out at `layout` while a saved project loads
   *  (#1230); null goes back to the default empty mat. */
  setLoadingLayout(layout: MatLayout | null): void {
    this.loadingLayout = layout ? { ...layout } : null;
  }

  /** Drop the established layout so the next render re-snaps — Scene calls
   *  this on a unit switch. */
  clearCachedMatLayout(): void {
    this.cachedMatLayout = null;
  }

  /** Project replacement (open / reset / reload restore): re-seed the cached
   *  layout from the incoming project's persisted placement (a copy, so the
   *  cache isn't aliased to the model) and drop the per-part bbox caches keyed
   *  by the old project's object identities. */
  resetForProject(persisted: MatLayout | null | undefined): void {
    this.cachedMatLayout = persisted ? { ...persisted } : null;
    this.matPieceBboxCache.clear();
    this.matLineBboxCache.clear();
  }

  renderMat(project: Project) {
    const layout = this.computeMatLayout(project);
    // The drawn mat depends only on layout / units / theme — never on the
    // parts — so reattach the cached group when none of those changed instead
    // of redrawing all the grid lines + ticks every render (#579). renderMat
    // runs first, so the reattached group keeps the bottom z-order.
    const matSig = JSON.stringify({
      w: layout.w,
      h: layout.h,
      cx: layout.cx,
      cy: layout.cy,
      units: this.host.getMatUnits(),
      strip: this.loadingStrip,
      zoom: this.loadingStrip ? this.scope.view.zoom : null,
      colors: [
        MAT_GREEN(),
        MAT_GRID_MINOR(),
        MAT_GRID_MAJOR(),
        MAT_GRID_AXIS(),
      ].join("|"),
    });
    if (this.matRenderCache && this.matRenderCache.sig === matSig) {
      this.rootLayer.addChild(this.matRenderCache.group);
      return;
    }
    const left = layout.cx - layout.w / 2;
    const top = layout.cy - layout.h / 2;
    const right = left + layout.w;
    const bottom = top + layout.h;

    // Outer mat: green fill with rounded corners.
    const outerRect = new this.scope.Rectangle(
      new this.scope.Point(left, top),
      new this.scope.Size(layout.w, layout.h),
    );
    const bg = new this.scope.Path.Rectangle(
      outerRect,
      new this.scope.Size(MAT_CORNER_RADIUS, MAT_CORNER_RADIUS),
    );
    let bgShape: paper.PathItem = bg;
    if (this.loadingStrip) {
      // The progress bar's strip, just below the grid's top line (#1230),
      // sized in screen pixels.
      const zoom = this.scope.view.zoom || 1;
      const strip = new this.scope.Path.Rectangle(
        new this.scope.Rectangle(
          left + MAT_PADDING,
          top + MAT_PADDING + MAT_BORDER_PX / 2 / zoom,
          layout.w - 2 * MAT_PADDING,
          LOAD_BAR_PX / zoom,
        ),
      );
      bgShape = bg.subtract(strip, { insert: false });
      bg.remove();
      strip.remove();
    }
    bgShape.fillColor = new this.scope.Color(MAT_GREEN());
    bgShape.strokeColor = null;
    bgShape.data = { ephemeral: true, kind: "mat-bg" };
    this.rootLayer.addChild(bgShape);

    // Inner working area sits inside the padding. The grid is clipped to
    // this rounded rectangle so lines don't poke past the corner curves.
    const innerLeft = left + MAT_PADDING;
    const innerTop = top + MAT_PADDING;
    const innerRight = right - MAT_PADDING;
    const innerBottom = bottom - MAT_PADDING;
    // The inner wrapper / border stays square-cornered even though the
    // outer mat is rounded — keeps the grid frame reading as a precise
    // measuring guide rather than echoing the soft outer shape.
    const innerRect = new this.scope.Rectangle(
      new this.scope.Point(innerLeft, innerTop),
      new this.scope.Size(innerRight - innerLeft, innerBottom - innerTop),
    );

    // Grid group, clipped to the square inner rect so the diagonals and any
    // overshooting grid segments stop at the inner border instead of
    // spilling into the padding.
    const clipShape = new this.scope.Path.Rectangle(innerRect);
    clipShape.clipMask = true;
    const gridGroup = new this.scope.Group([clipShape]);
    gridGroup.data = { ephemeral: true };

    // Three-tier grid driven by the active unit system. Each tier paints
    // on top of the previous so the heaviest lines win at intersections.
    //   Metric:   1 cm minor, 5 cm major, 10 cm extra.
    //   Imperial: 1/4" minor, 1" major, 6" extra.
    const {
      minor: minorStep,
      major: majorStep,
      extra: extraStep,
    } = this.host.gridSteps();

    const minor = this.buildGridPath(
      innerLeft,
      innerTop,
      innerRight,
      innerBottom,
      minorStep,
    );
    if (minor) {
      minor.strokeColor = new this.scope.Color(MAT_GRID_MINOR());
      minor.strokeWidth = 0.3;
      minor.strokeScaling = false;
      minor.fillColor = null;
      gridGroup.addChild(minor);
    }
    const major = this.buildGridPath(
      innerLeft,
      innerTop,
      innerRight,
      innerBottom,
      majorStep,
    );
    if (major) {
      major.strokeColor = new this.scope.Color(MAT_GRID_MAJOR());
      major.strokeWidth = 0.5;
      major.strokeScaling = false;
      major.fillColor = null;
      gridGroup.addChild(major);
    }
    const extra = this.buildGridPath(
      innerLeft,
      innerTop,
      innerRight,
      innerBottom,
      extraStep,
    );
    if (extra) {
      extra.strokeColor = new this.scope.Color(MAT_GRID_AXIS());
      extra.strokeWidth = 0.8;
      extra.strokeScaling = false;
      extra.fillColor = null;
      gridGroup.addChild(extra);
    }

    // Diagonal reference lines — match the angled-cut guides printed on
    // most cutting mats. 30/45/60° fan rising from the bottom-left corner,
    // plus a 45° line from the bottom-right corner that stops at the apex
    // where it meets the bottom-left 45° (so the two 45°s form a peak at
    // the horizontal midpoint instead of crossing into an X across the
    // mat).
    const diagonalLength =
      Math.hypot(innerRight - innerLeft, innerBottom - innerTop) * 1.2;
    const diagonalSegs: string[] = [];
    for (const angleDeg of [30, 45, 60]) {
      const rad = (angleDeg * Math.PI) / 180;
      // Paper.js uses y-down; flipping sin() points "angleDeg up from the
      // horizontal" so 30° / 45° / 60° read as a fan rising into the mat.
      const ex = innerLeft + Math.cos(rad) * diagonalLength;
      const ey = innerBottom - Math.sin(rad) * diagonalLength;
      diagonalSegs.push(`M ${innerLeft} ${innerBottom} L ${ex} ${ey}`);
    }
    // Apex where the two 45° diagonals meet: at the horizontal midpoint of
    // the inner rect, half the inner width up from the bottom edge. For
    // any landscape mat (w > 2h is the only failure case, and our forced
    // landscape standard sizes all have w/h ≤ 1.5), the apex sits inside
    // the inner rect, so no clipping is needed.
    const apexX = (innerLeft + innerRight) / 2;
    const apexY = innerBottom - (innerRight - innerLeft) / 2;
    diagonalSegs.push(`M ${innerRight} ${innerBottom} L ${apexX} ${apexY}`);
    try {
      const diagonalPath = new this.scope.CompoundPath({
        pathData: diagonalSegs.join(" "),
      });
      diagonalPath.strokeColor = new this.scope.Color(MAT_GRID_AXIS());
      diagonalPath.strokeWidth = 0.5;
      diagonalPath.strokeScaling = false;
      diagonalPath.fillColor = null;
      gridGroup.addChild(diagonalPath);
    } catch {
      /* ignore */
    }

    // Numeric labels at every cm (metric) / inch (imperial). Drop them
    // along the bottom and left edges of the inner rect so they overlap
    // the border + the grid lines passing through them — same convention
    // as the printed scale on a real cutting mat.
    this.appendMatLabels(
      innerLeft,
      innerTop,
      innerRight,
      innerBottom,
      gridGroup,
    );
    // Brand markings in the upper-right, sized in the active units (3 × 2
    // cm metric, 3 × 2 in imperial). Top-right corner snaps to the
    // second-to-last full grid square's top-right intersection so the
    // brand reads as part of the printed mat scale.
    this.appendMatBrand(
      innerLeft,
      innerTop,
      innerRight,
      innerBottom,
      gridGroup,
    );
    this.rootLayer.addChild(gridGroup);

    // Inner border stroke — sits on top of the grid so the boundary reads
    // crisply, mirroring the printed frame line on a real cutting mat.
    // Square-cornered on purpose; the outer mat alone carries the rounding.
    const border = new this.scope.Path.Rectangle(innerRect);
    border.strokeColor = new this.scope.Color(MAT_GRID_AXIS());
    border.strokeWidth = MAT_BORDER_PX;
    border.strokeScaling = false;
    border.fillColor = null;
    border.data = { ephemeral: true };
    this.rootLayer.addChild(border);

    // Fine-grain tick marks along the four edges of the inner rect — small
    // perpendicular dashes that cross the border, half inside the grid
    // area and half outside in the padding. Added to the root layer (not
    // the clipped grid group) so the outside half stays visible.
    this.appendMatEdgeTicks(innerLeft, innerTop, innerRight, innerBottom);

    // "made online" tagline in the bottom padding band, right-aligned to
    // the grid's right edge and vertically centered in the band. Goes on
    // the root layer (the grid group is clipped to the inner rect, which
    // would hide anything outside it).
    this.appendMatTagline(innerRight, innerBottom);

    // Wrap everything just drawn (it's all of rootLayer's children — renderMat
    // runs first) into one ephemeral group and cache it for reuse next render.
    const matGroup = new this.scope.Group(this.rootLayer.removeChildren());
    matGroup.data = { ephemeral: true, kind: "mat" };
    this.rootLayer.addChild(matGroup);
    this.matRenderCache = { sig: matSig, group: matGroup };
  }

  /** Tiny "made online" tagline tucked into the bottom padding band of
   *  the mat. Right edge of the text aligns with the grid's right edge;
   *  text sits vertically centered between the grid bottom and the mat's
   *  outer bottom edge. */
  private appendMatTagline(innerRight: number, innerBottom: number) {
    // All-caps reads as a small printed credit; tucked into the band at
    // ~1/3 the padding height so it stays subtle.
    const fontSize = MAT_PADDING * 0.32;
    // PointText's `point` is the baseline, not the visual center. Shift
    // down by ~1/3 the font size so the glyphs straddle the band's
    // mid-line instead of sitting above it.
    const baselineY = innerBottom + MAT_PADDING / 2 + fontSize / 3;
    try {
      const t = new this.scope.PointText({
        point: [innerRight, baselineY],
        content: "MADE BY HANDS",
        fillColor: new this.scope.Color(MAT_GRID_AXIS()),
        fontFamily: "sans-serif",
        fontSize,
        justification: "right",
      });
      t.data = { ephemeral: true };
      this.rootLayer.addChild(t);
    } catch {
      /* font / text not available — silent */
    }
  }

  /** Smallest standard cutting-mat size that envelops every visible part's
   *  bounding box (and every visible construction line — issue #110),
   *  plus a working margin. Both portrait and landscape orientations of
   *  each candidate are tried; the mat is centered on the bbox so empty
   *  designs default to the smallest size at the origin. */
  /** Parse `pathData` just far enough to read its bounding box, then discard
   *  the path. Returns null on malformed data. */
  private matBboxOf(pathData: string): BBox | null {
    try {
      const p = new this.scope.CompoundPath({ pathData, insert: false });
      const b = p.bounds;
      const out = {
        minX: b.x,
        minY: b.y,
        maxX: b.x + b.width,
        maxY: b.y + b.height,
      };
      p.remove();
      return out;
    } catch {
      return null;
    }
  }

  computeMatLayout(project: Project): {
    w: number;
    h: number;
    cx: number;
    cy: number;
  } {
    // Pick the size list that matches the active unit system so metric
    // users see A-series sizes and imperial users see standard mat sizes.
    const sizes =
      this.host.getMatUnits() === "in"
        ? STANDARD_MAT_SIZES_MM_IMPERIAL
        : STANDARD_MAT_SIZES_MM_METRIC;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    // Each entry holds both the part's bbox (for cheap pre-filter against
    // the brand rect) and the actual paper-path geometry (for the real
    // shape-vs-rect intersection test in brandOverlapsAny — a thin part
    // with a wide bbox shouldn't block a smaller mat just because its
    // bbox grazes the brand corner).
    const partGeoms: {
      bbox: BBox;
      getPath: () => paper.CompoundPath | null;
    }[] = [];
    // The mat envelops EVERY object, hidden ones included, and every linked
    // part at both of its positions (#1183). It is the work surface the
    // objects live on, so its size mustn't follow what happens to be shown:
    // hiding a part or a guide, or toggling Assembled / Spread, used to shrink
    // or grow it out from under the user.
    const seenPieceIds = new Set<string>();
    for (const piece of project.pieces) {
      seenPieceIds.add(piece.id);
      // Bbox is cached by piece identity (#579) so unchanged parts aren't
      // re-parsed every render. The path itself is built lazily (and memoized
      // per render) — only the rare part grazing the brand corner needs it.
      const cached = this.matPieceBboxCache.get(piece.id);
      let bbox = cached && cached.piece === piece ? cached.bbox : null;
      if (!bbox) {
        bbox = this.matBboxOf(piece.shapePathData);
        if (bbox) this.matPieceBboxCache.set(piece.id, { piece, bbox });
      }
      if (!bbox) continue;
      // A linked part sits at its SPREAD (cut-layout) position in one view and
      // at its construction position in the other (#652). The mat covers both
      // (#1183), so switching views never resizes it. The cached bbox / path
      // are of the un-offset `shapePathData`; the Spread copy is shifted by the
      // part's `layout`. Unlinked / un-laid-out parts have one position.
      const layout = piece.constructionOutline?.layout;
      const ldx = layout?.dx ?? 0;
      const ldy = layout?.dy ?? 0;
      const offsets: [number, number][] =
        ldx !== 0 || ldy !== 0
          ? [
              [0, 0],
              [ldx, ldy],
            ]
          : [[0, 0]];
      for (const [dx, dy] of offsets) {
        const pbbox =
          dx !== 0 || dy !== 0
            ? {
                minX: bbox.minX + dx,
                minY: bbox.minY + dy,
                maxX: bbox.maxX + dx,
                maxY: bbox.maxY + dy,
              }
            : bbox;
        let path: paper.CompoundPath | null | undefined;
        partGeoms.push({
          bbox: pbbox,
          getPath: () => {
            if (path === undefined) {
              try {
                path = new this.scope.CompoundPath({
                  pathData: piece.shapePathData,
                  insert: false,
                });
                if (path && (dx !== 0 || dy !== 0)) {
                  path.translate(new this.scope.Point(dx, dy));
                }
              } catch {
                path = null;
              }
            }
            return path;
          },
        });
        minX = Math.min(minX, pbbox.minX);
        minY = Math.min(minY, pbbox.minY);
        maxX = Math.max(maxX, pbbox.maxX);
        maxY = Math.max(maxY, pbbox.maxY);
      }
    }
    if (this.matPieceBboxCache.size > seenPieceIds.size) {
      for (const id of [...this.matPieceBboxCache.keys()]) {
        if (!seenPieceIds.has(id)) this.matPieceBboxCache.delete(id);
      }
    }
    // Construction (guide) lines extend the envelope the same way pieces
    // do (issue #110) — a layout drawn entirely from guides still needs
    // a mat big enough to hold it. They're intentionally left out of
    // `partGeoms` so the brand-overlap check stays piece-only: a thin
    // dashed guide running under the brand sticker isn't a real cover-up.
    const seenLineIds = new Set<string>();
    for (const line of projectConstructionLines(project)) {
      seenLineIds.add(line.id);
      const cached = this.matLineBboxCache.get(line.id);
      let bbox = cached && cached.line === line ? cached.bbox : null;
      if (!bbox) {
        bbox = this.matBboxOf(line.pathData);
        if (bbox) this.matLineBboxCache.set(line.id, { line, bbox });
      }
      if (!bbox) continue;
      if (bbox.maxX - bbox.minX === 0 && bbox.maxY - bbox.minY === 0) continue;
      minX = Math.min(minX, bbox.minX);
      minY = Math.min(minY, bbox.minY);
      maxX = Math.max(maxX, bbox.maxX);
      maxY = Math.max(maxY, bbox.maxY);
    }
    if (this.matLineBboxCache.size > seenLineIds.size) {
      for (const id of [...this.matLineBboxCache.keys()]) {
        if (!seenLineIds.has(id)) this.matLineBboxCache.delete(id);
      }
    }
    // Tracing images extend the envelope too (issue #531) — a reference
    // photo placed off to the side still needs a mat big enough to show
    // it. Use the rotated AABB (`tracingImageBounds`) so a turned image's
    // footprint is fully covered. Like construction lines, images are
    // left out of `partGeoms` so the brand-overlap check stays piece-only:
    // a reference photo sitting under the brand sticker isn't a real
    // cover-up the way a leather part would be.
    for (const image of projectTracingImages(project)) {
      const b = tracingImageBounds(image);
      if (b.width === 0 && b.height === 0) continue;
      minX = Math.min(minX, b.x);
      minY = Math.min(minY, b.y);
      maxX = Math.max(maxX, b.x + b.width);
      maxY = Math.max(maxY, b.y + b.height);
    }

    // First non-empty render: seed the cache with the default mat
    // centered at origin (matches what was rendered while the project
    // was empty). This way drawing the first part doesn't shove the mat
    // around to re-center on the part — the part stays where the user
    // drew it on the existing mat. If the part is drawn far enough off-
    // origin that the default mat can't envelop it with the anchor
    // preserved, the cache-reuse path below clears the cache and falls
    // through to the centered re-snap.
    const seededDefault = !this.cachedMatLayout && isFinite(minX);
    if (seededDefault) {
      const def = this.pickLargestMatFittingCanvas(sizes);
      this.cachedMatLayout = { w: def.w, h: def.h, cx: 0, cy: 0 };
    }

    // Cache reuse / resize path. Keeps the bottom-left anchor (innerLeft,
    // innerBottom) fixed so grid-snapped parts stay on their lines, then
    // re-picks the smallest standard size whose inner area covers the
    // parts with at least the working clearance. The same code path
    // handles three outcomes:
    //   • picked size == cached size → return cached unchanged.
    //   • picked size  > cached size → upgrade (new part overflowed).
    //   • picked size  < cached size → downgrade (parts now fit in a
    //     smaller mat after the user moved them in).
    // Falls through to a full re-snap only when parts have moved past the
    // cached anchor on the left or below (where we'd have to shift the
    // anchor and break snap alignments to keep them inside the mat).
    if (this.cachedMatLayout && isFinite(minX)) {
      const cl = this.cachedMatLayout;
      const innerLeft = cl.cx - cl.w / 2 + MAT_PADDING;
      const innerBottom = cl.cy + cl.h / 2 - MAT_PADDING;
      const eps = 1e-6;
      const step = this.host.gridSteps().minor;
      const overflowsLeft = minX < innerLeft - eps;
      const overflowsBottom = maxY > innerBottom + eps;
      // On the very first part, the cache is still the synthetic origin-centred
      // default seeded just above — there are no existing parts to keep fixed.
      // If that first part spills past the default's left / bottom edge, fall
      // through to a fresh centred re-snap so the grid lattice phase-aligns to
      // the part (its edges land on grid lines). Growing by whole grid steps to
      // hold parts in place only makes sense once a real layout is established.
      if (seededDefault && (overflowsLeft || overflowsBottom)) {
        this.cachedMatLayout = null;
      } else {
        // Keep existing parts fixed on the mat when it has to grow (#785): when
        // content overflows on the left / bottom, extend the grid anchor by
        // WHOLE grid steps to cover it rather than falling through to a centred
        // re-snap. Stepping by the grid pitch preserves the lattice phase, so
        // every existing part stays on its own grid line instead of the whole
        // layout shifting to re-centre on the new bounding box. (Right / top
        // overflow already grew the mat from this fixed anchor.)
        const anchorLeft = overflowsLeft
          ? innerLeft - Math.ceil((innerLeft - minX) / step) * step
          : innerLeft;
        const anchorBottom = overflowsBottom
          ? innerBottom + Math.ceil((maxY - innerBottom) / step) * step
          : innerBottom;
        // Resize threshold is "do parts fit inside the grid area?" — no extra
        // working clearance, so we only step up when something overflows and
        // step down only when a smaller mat still contains every part. Reject
        // any candidate where the top-right brand block would overlap a part;
        // step up to the next size that clears it.
        const reqLW = maxX - anchorLeft + 2 * MAT_PADDING;
        const reqLH = anchorBottom - minY + 2 * MAT_PADDING;
        for (const size of sizes) {
          const lW = size.h;
          const lH = size.w;
          if (lW < reqLW || lH < reqLH) continue;
          if (
            this.brandOverlapsAny(lW, lH, anchorLeft, anchorBottom, partGeoms)
          ) {
            continue;
          }
          const unchanged =
            lW === cl.w &&
            lH === cl.h &&
            Math.abs(anchorLeft - innerLeft) < eps &&
            Math.abs(anchorBottom - innerBottom) < eps;
          if (unchanged) return cl;
          const matLeft = anchorLeft - MAT_PADDING;
          const matBottom = anchorBottom + MAT_PADDING;
          const resized = {
            w: lW,
            h: lH,
            cx: matLeft + lW / 2,
            cy: matBottom - lH / 2,
          };
          this.cachedMatLayout = resized;
          return resized;
        }
        // Parts exceed every standard size even from the extended anchor — fall
        // through to a full re-snap below.
        this.cachedMatLayout = null;
      }
    }
    if (!isFinite(minX)) {
      // Empty project: drop the cached layout so the next time parts
      // appear we re-snap fresh, then return the largest standard mat
      // that fits the canvas at 1:1 (real-size) zoom — so the empty
      // canvas opens with the most generous mat the user's screen can
      // actually display at real size.
      this.cachedMatLayout = null;
      if (this.loadingLayout) return { ...this.loadingLayout };
      const def = this.pickLargestMatFittingCanvas(sizes);
      return { w: def.w, h: def.h, cx: 0, cy: 0 };
    }
    // Pick the smallest standard mat that envelops the parts with a 20 mm
    // working clearance on all sides (plus the padding band). For each
    // candidate size we also try the snapped layout and check that the
    // brand block in the top-right corner doesn't overlap any part — if
    // it would, step up to the next size that clears it.
    const margin = 20 + MAT_PADDING;
    const partsW = maxX - minX;
    const partsH = maxY - minY;
    const reqW = partsW + margin * 2;
    const reqH = partsH + margin * 2;
    const partsCenterX = (minX + maxX) / 2;
    const partsCenterY = (minY + maxY) / 2;
    const step = this.host.gridSteps().minor;

    let layout: { w: number; h: number; cx: number; cy: number } | null = null;
    for (const size of sizes) {
      const lW = size.h;
      const lH = size.w;
      if (lW < reqW || lH < reqH) continue;
      const snapped = this.snapMatLayout(
        lW,
        lH,
        partsCenterX,
        partsCenterY,
        minX,
        maxY,
        step,
      );
      if (
        this.brandOverlapsAny(
          lW,
          lH,
          snapped.innerLeft,
          snapped.innerBottom,
          partGeoms,
        )
      ) {
        continue;
      }
      layout = { w: lW, h: lH, cx: snapped.cx, cy: snapped.cy };
      break;
    }
    if (!layout) {
      // Parts exceed every standard size (or the brand kept overlapping
      // even at the largest). Fall back to a custom landscape size that
      // envelops them and accept whatever brand placement that yields.
      const last = sizes[sizes.length - 1]!;
      const matW = Math.max(last.h, reqW);
      const matH = Math.max(last.w, reqH);
      const snapped = this.snapMatLayout(
        matW,
        matH,
        partsCenterX,
        partsCenterY,
        minX,
        maxY,
        step,
      );
      layout = { w: matW, h: matH, cx: snapped.cx, cy: snapped.cy };
    }
    // Cache so subsequent renders (after moving parts, undo, redo, etc.)
    // keep the mat anchored at the same spot until units change or the
    // project is replaced.
    this.cachedMatLayout = layout;
    return layout;
  }

  /** Snaps a candidate mat layout so the parts' bbox bottom-left lands on
   *  the nearest grid intersection (within ±½ step on each axis). Pulled
   *  out as a helper so both the size-selection loop and the
   *  oversize-fallback branch can use it. */
  private snapMatLayout(
    matW: number,
    matH: number,
    partsCenterX: number,
    partsCenterY: number,
    partsMinX: number,
    partsMaxY: number,
    step: number,
  ): { cx: number; cy: number; innerLeft: number; innerBottom: number } {
    const initialInnerLeft = partsCenterX - matW / 2 + MAT_PADDING;
    const initialInnerBottom = partsCenterY + matH / 2 - MAT_PADDING;
    const kX = Math.round((partsMinX - initialInnerLeft) / step);
    const innerLeft = partsMinX - kX * step;
    const kY = Math.round((initialInnerBottom - partsMaxY) / step);
    const innerBottom = partsMaxY + kY * step;
    return {
      cx: innerLeft - MAT_PADDING + matW / 2,
      cy: innerBottom + MAT_PADDING - matH / 2,
      innerLeft,
      innerBottom,
    };
  }

  /** True when the brand block (positioned at the upper-right of a
   *  candidate mat layout) overlaps a part's *visible* shape. Used when
   *  picking a mat size to avoid covering a part with the brand sticker
   *  — when it would, callers step up to the next standard size. The
   *  bbox is only a fast pre-filter; the real test runs against the
   *  part's actual geometry, so a thin part with a wide bbox doesn't
   *  block a smaller mat just because its bbox grazes the brand corner. */
  private brandOverlapsAny(
    matW: number,
    matH: number,
    innerLeft: number,
    innerBottom: number,
    parts: {
      bbox: BBox;
      getPath: () => paper.CompoundPath | null;
    }[],
  ): boolean {
    if (parts.length === 0) return false;
    const step = this.host.gridSteps().minor;
    const innerW = matW - 2 * MAT_PADDING;
    const innerH = matH - 2 * MAT_PADDING;
    const eps = 1e-6;
    const fullX = Math.floor(innerW / step + eps);
    const fullY = Math.floor(innerH / step + eps);
    if (fullX < 2 || fullY < 2) return false;
    const brandRightX = innerLeft + (fullX - 1) * step;
    const brandTopY = innerBottom - (fullY - 1) * step;
    const brandW = this.host.getMatUnits() === "in" ? 50.8 : 40;
    const brandH = this.host.getMatUnits() === "in" ? 25.4 : 20;
    const brandLeft = brandRightX - brandW;
    const brandBottom = brandTopY + brandH;
    // If the brand wouldn't fit / render, it can't overlap.
    if (brandLeft < innerLeft || brandBottom > innerBottom) return false;
    // Tolerance: allow up to 1.5 mm of penetration on each side before
    // the brand counts as "overlapped". Lets a part graze the corner of
    // the sticker without forcing the mat up to the next standard size.
    const tol = 1.5;
    const rectLeft = brandLeft + tol;
    const rectRight = brandRightX - tol;
    const rectTop = brandTopY + tol;
    const rectBottom = brandBottom - tol;
    if (rectLeft >= rectRight || rectTop >= rectBottom) return false;
    const brandRect = new this.scope.Path.Rectangle({
      from: [rectLeft, rectTop],
      to: [rectRight, rectBottom],
      insert: false,
    });
    try {
      for (const part of parts) {
        const bb = part.bbox;
        // Cheap reject — bbox doesn't even touch the brand rect.
        if (
          bb.maxX <= rectLeft ||
          bb.minX >= rectRight ||
          bb.maxY <= rectTop ||
          bb.minY >= rectBottom
        ) {
          continue;
        }
        // Only the rare part grazing the brand corner needs its real
        // geometry — parse it lazily here (#579).
        const path = part.getPath();
        if (!path) continue;
        // Outline-vs-outline crossing covers the common case (the part
        // edge cuts through the brand rect).
        if (path.intersects(brandRect)) return true;
        // No edge crossing — either no overlap, or one shape fully
        // contains the other. Sample the rect center against the part:
        // if it's inside, the brand sits entirely under the part.
        if (
          path.contains(
            new this.scope.Point(
              (rectLeft + rectRight) / 2,
              (rectTop + rectBottom) / 2,
            ),
          )
        ) {
          return true;
        }
      }
      return false;
    } finally {
      brandRect.remove();
    }
  }

  /** Fine-grain tick marks along all four edges of the inner rect — for
   *  metric, minor ticks every 1 mm and major every 5 mm; for imperial,
   *  minor every 1/16" and major every 1/8". Each tick crosses the inner
   *  border line both inside (into the grid area) and outside (into the
   *  padding band). Added directly to the root layer so the outside half
   *  isn't cut off by the grid-area clip. */
  private appendMatEdgeTicks(
    innerLeft: number,
    innerTop: number,
    innerRight: number,
    innerBottom: number,
  ) {
    const isMetric = this.host.getMatUnits() !== "in";
    const minorStep = isMetric ? 1 : 25.4 / 16;
    const majorStep = isMetric ? 5 : 25.4 / 8;
    const minorHalf = 0.5; // mm: tick crosses border by this much each side
    const majorHalf = 1.1;
    const majorRatio = Math.round(majorStep / minorStep); // every Nth tick

    const minorParts: string[] = [];
    const majorParts: string[] = [];
    const eps = 1e-6;

    // Skip ticks that would land exactly on a corner of the inner rect —
    // those four crossings are owned by the corners themselves and the
    // extra dashes look like artifacts.
    const cornerEps = minorStep / 4;
    const atCornerX = (x: number) =>
      Math.abs(x - innerLeft) < cornerEps ||
      Math.abs(x - innerRight) < cornerEps;
    const atCornerY = (y: number) =>
      Math.abs(y - innerTop) < cornerEps ||
      Math.abs(y - innerBottom) < cornerEps;

    // Vertical ticks along the top + bottom edges (x-axis).
    const xCount = Math.floor((innerRight - innerLeft) / minorStep + eps);
    for (let i = 0; i <= xCount; i++) {
      const x = innerLeft + i * minorStep;
      if (atCornerX(x)) continue;
      const isMajor = i % majorRatio === 0;
      const half = isMajor ? majorHalf : minorHalf;
      const target = isMajor ? majorParts : minorParts;
      target.push(
        `M ${x} ${innerTop - half} L ${x} ${innerTop + half}`,
        `M ${x} ${innerBottom - half} L ${x} ${innerBottom + half}`,
      );
    }
    // Horizontal ticks along the left + right edges (y-axis).
    const yCount = Math.floor((innerBottom - innerTop) / minorStep + eps);
    for (let i = 0; i <= yCount; i++) {
      const y = innerBottom - i * minorStep;
      if (atCornerY(y)) continue;
      const isMajor = i % majorRatio === 0;
      const half = isMajor ? majorHalf : minorHalf;
      const target = isMajor ? majorParts : minorParts;
      target.push(
        `M ${innerLeft - half} ${y} L ${innerLeft + half} ${y}`,
        `M ${innerRight - half} ${y} L ${innerRight + half} ${y}`,
      );
    }

    // Both tiers share the major-grid stroke weight so the ticks look
    // like a fine ruler scale — only the length distinguishes major from
    // minor.
    const tickStrokeWidth = 0.5;
    if (minorParts.length > 0) {
      try {
        const minorTicks = new this.scope.CompoundPath({
          pathData: minorParts.join(" "),
        });
        minorTicks.strokeColor = new this.scope.Color(MAT_GRID_AXIS());
        minorTicks.strokeWidth = tickStrokeWidth;
        minorTicks.strokeScaling = false;
        minorTicks.fillColor = null;
        minorTicks.data = { ephemeral: true };
        this.rootLayer.addChild(minorTicks);
      } catch {
        /* ignore */
      }
    }
    if (majorParts.length > 0) {
      try {
        const majorTicks = new this.scope.CompoundPath({
          pathData: majorParts.join(" "),
        });
        majorTicks.strokeColor = new this.scope.Color(MAT_GRID_AXIS());
        majorTicks.strokeWidth = tickStrokeWidth;
        majorTicks.strokeScaling = false;
        majorTicks.fillColor = null;
        majorTicks.data = { ephemeral: true };
        this.rootLayer.addChild(majorTicks);
      } catch {
        /* ignore */
      }
    }
  }

  /** CompoundPath of grid lines anchored at the bottom-left corner of the
   *  inner rectangle — vertical lines step rightward from `left`,
   *  horizontal lines step upward from `bottom`. Matches the printed
   *  scales on real cutting mats, which originate at one corner rather
   *  than the center. One CompoundPath per tier keeps the scene tree
   *  small even on the biggest mats. */
  private buildGridPath(
    left: number,
    top: number,
    right: number,
    bottom: number,
    step: number,
  ): paper.CompoundPath | null {
    const parts: string[] = [];
    for (let x = left; x <= right + 1e-6; x += step) {
      parts.push(`M ${x} ${top} L ${x} ${bottom}`);
    }
    for (let y = bottom; y >= top - 1e-6; y -= step) {
      parts.push(`M ${left} ${y} L ${right} ${y}`);
    }
    if (parts.length === 0) return null;
    try {
      return new this.scope.CompoundPath({ pathData: parts.join(" ") });
    } catch {
      return null;
    }
  }

  /** Numeric labels along the bottom + left edges of the inner mat area.
   *  Metric labels each cm ("1, 2, 3 …") because 1 cm is the natural eye-
   *  reading unit on metric mats; imperial labels each inch ("1, 2, 3 …")
   *  for the same reason. Labels sit just inside the inner edges so they
   *  overlap the border line and the perpendicular grid lines crossing
   *  through their position, matching the printed scale convention on
   *  physical cutting mats. */
  private appendMatLabels(
    innerLeft: number,
    innerTop: number,
    innerRight: number,
    innerBottom: number,
    group: paper.Group,
  ) {
    // Step in mm between successive labels — 10 mm (1 cm) for metric,
    // 25.4 mm (1 in) for imperial. Each labeled tick is one unit.
    const stepMm = this.host.getMatUnits() === "in" ? 25.4 : 10;
    const fontSize = 4; // mm in project space
    const inset = 2; // mm offset from the inner edge so labels read clean
    const color = new this.scope.Color(MAT_GRID_AXIS());
    const epsilon = 0.5;

    // X labels along the bottom edge (skip "0" — the corner is the origin
    // and labeling it would clutter the bottom-left).
    let i = 1;
    for (let x = innerLeft + stepMm; x < innerRight - epsilon; x += stepMm) {
      try {
        const t = new this.scope.PointText({
          point: [x, innerBottom - inset],
          content: String(i),
          fillColor: color,
          fontFamily: "sans-serif",
          fontSize,
          justification: "center",
        });
        t.data = { ephemeral: true };
        group.addChild(t);
      } catch {
        /* ignore — font / text not available */
      }
      i += 1;
    }

    // Y labels along the left edge — same numbering scheme, oriented to
    // run upward from the bottom-left corner.
    i = 1;
    for (let y = innerBottom - stepMm; y > innerTop + epsilon; y -= stepMm) {
      try {
        const t = new this.scope.PointText({
          // Baseline-shift by ~1/3 font height so the label visually
          // straddles the grid line instead of sitting on top of it.
          point: [innerLeft + inset, y + fontSize / 3],
          content: String(i),
          fillColor: color,
          fontFamily: "sans-serif",
          fontSize,
          justification: "left",
        });
        t.data = { ephemeral: true };
        group.addChild(t);
      } catch {
        /* ignore */
      }
      i += 1;
    }
  }

  /** "Draw Leather" brand block dropped on the upper-right of the mat —
   *  4 × 2 cm in metric, 2 × 1 in in imperial. The top-
   *  right corner of the brand box aligns with the second-to-last full
   *  grid intersection from the upper-right of the inner rect so the
   *  brand reads as a printed part of the mat scale. Four stacked lines:
   *  brand name, "cutting mat", mat dimensions, and unit system. */
  private appendMatBrand(
    innerLeft: number,
    innerTop: number,
    innerRight: number,
    innerBottom: number,
    group: paper.Group,
  ) {
    const step = this.host.gridSteps().minor;
    const innerW = innerRight - innerLeft;
    const innerH = innerBottom - innerTop;
    const eps = 1e-6;
    // Count the full grid squares that actually fit inside the inner rect
    // (any partial cell at the right / top is skipped).
    const fullX = Math.floor(innerW / step + eps);
    const fullY = Math.floor(innerH / step + eps);
    if (fullX < 2 || fullY < 2) return; // not enough mat for a brand block

    // Top-right of the "second-to-last" full grid square (one step
    // inward from the corner-most square on both axes).
    const brandRightX = innerLeft + (fullX - 1) * step;
    const brandTopY = innerBottom - (fullY - 1) * step;
    const brandW = this.host.getMatUnits() === "in" ? 50.8 : 40; // 2 in / 4 cm
    const brandH = this.host.getMatUnits() === "in" ? 25.4 : 20; // 1 in / 2 cm
    const brandLeftX = brandRightX - brandW;
    const brandBottomY = brandTopY + brandH;
    if (brandLeftX < innerLeft || brandBottomY > innerBottom) return;

    // Filled outline — the green fill paints over the grid lines and
    // diagonals that would otherwise show through the brand box, so the
    // brand reads as a clean printed sticker on the mat instead of
    // floating type over busy lines. Tiny corner radius softens the
    // sticker shape without making it look bubbly.
    try {
      const brandCornerRadius = brandH * 0.06;
      const frameRect = new this.scope.Rectangle(
        new this.scope.Point(brandLeftX, brandTopY),
        new this.scope.Point(brandRightX, brandBottomY),
      );
      const frame = new this.scope.Path.Rectangle(
        frameRect,
        new this.scope.Size(brandCornerRadius, brandCornerRadius),
      );
      frame.fillColor = new this.scope.Color(MAT_GREEN());
      frame.strokeColor = new this.scope.Color(MAT_GRID_AXIS());
      frame.strokeWidth = 0.5;
      frame.strokeScaling = false;
      frame.data = { ephemeral: true };
      group.addChild(frame);
    } catch {
      /* ignore */
    }

    // Three stacked rows: brand URL, dimensions ("40x20" + unit pill,
    // bold and sized to fill the pill's outer height), and a "cutting
    // mat" caption at the bottom.
    const outerMatW = innerW + 2 * MAT_PADDING;
    const outerMatH = innerH + 2 * MAT_PADDING;
    const aSeriesName =
      this.host.getMatUnits() === "in"
        ? null
        : this.matchAseriesName(outerMatW, outerMatH);
    const numberText =
      this.host.getMatUnits() === "in"
        ? `${this.formatInches(outerMatW)} × ${this.formatInches(outerMatH)}`
        : (aSeriesName ??
          `${Math.round(outerMatW / 10)} × ${Math.round(outerMatH / 10)}`);
    const unitText = this.host.getMatUnits() === "in" ? "in" : "metric";

    // Asymmetric insets: vertical stays tight, but horizontal gets a bump
    // so the visual gap from the box edge to "drawleather.com" matches
    // the visual gap above the name (which is bigger than the bare
    // vertical inset because the slot centers the text vertically).
    const vInset = brandH * 0.08;
    const hInset = brandH * 0.14;
    const usableW = brandW - hInset * 2;
    const usableH = brandH - vInset * 2;
    const slotGap = usableH * 0.04;
    const heightForRows = usableH - slotGap * 2;
    const nameSlotH = heightForRows * 0.4;
    const dimsSlotH = heightForRows * 0.4;
    const captionSlotH = heightForRows * 0.2;

    // Brand-name size — capped by its vertical slot and the box width.
    const nameNatW = this.measureTextWidth("drawleather.com", 700, 10);
    const nameMaxByWidth = nameNatW > 0 ? (10 * usableW) / nameNatW : nameSlotH;
    const nameSize = Math.min(nameSlotH, nameMaxByWidth);

    // Dimensions row: bold number text whose font size matches the pill's
    // outer height, plus a unit pill (capsule with inverted colors).
    // Pill outer height = pillTextSize * (1 + 2 * padY ratio). Solving for
    // pillTextSize so the pill outer height = numberSize keeps the number
    // and pill visually the same height.
    const pillPadXRatio = 0.38; // horizontal padding inside pill, in pill-text units
    const pillPadYRatio = 0.15; // vertical padding inside pill, in pill-text units
    const interGapRatio = 0.32; // gap between number and pill, in number-size units
    const numNatW = this.measureTextWidth(numberText, 700, 10);
    const unitNatW = this.measureTextWidth(unitText, 700, 10);
    // At a given pillTextSize T:
    //   pillH = T * (1 + 2 * pillPadYRatio)
    //   numberSize = pillH
    //   numberW = numNatW * numberSize / 10
    //   unitW = unitNatW * T / 10
    //   pillW = unitW + 2 * T * pillPadXRatio
    //   interGap = numberSize * interGapRatio
    //   rowW = numberW + interGap + pillW
    // Solve rowW as a linear function of T:
    const pillHFactor = 1 + 2 * pillPadYRatio; // numberSize / T
    const rowWPerT =
      (numNatW / 10) * pillHFactor + // numberW per T
      pillHFactor * interGapRatio + // interGap per T
      unitNatW / 10 +
      2 * pillPadXRatio;
    const dimsTByH = dimsSlotH / pillHFactor;
    const dimsTByW = rowWPerT > 0 ? (usableW * 0.92) / rowWPerT : dimsTByH;
    const pillTextSize = Math.min(dimsTByH, dimsTByW);
    const pillPadX = pillTextSize * pillPadXRatio;
    const pillPadY = pillTextSize * pillPadYRatio;
    const pillH = pillTextSize + pillPadY * 2;
    const numberSize = pillH;
    const numW = (numNatW * numberSize) / 10;
    const unitW = (unitNatW * pillTextSize) / 10;
    const pillW = unitW + pillPadX * 2;
    const interGap = numberSize * interGapRatio;
    const rowW = numW + interGap + pillW;

    // Caption row at the bottom — "cutting mat" in lighter weight.
    const captionText = "cutting mat";
    const captionNatW = this.measureTextWidth(captionText, 400, 10);
    const captionMaxByH = captionSlotH * 0.95;
    const captionMaxByW =
      captionNatW > 0 ? (10 * usableW * 0.92) / captionNatW : captionMaxByH;
    const captionSize = Math.min(captionMaxByH, captionMaxByW);

    // Layout vertical slots inside the brand box.
    const slotNameTop = brandTopY + vInset;
    const slotNameBottom = slotNameTop + nameSlotH;
    const slotDimsTop = slotNameBottom + slotGap;
    const slotDimsBottom = slotDimsTop + dimsSlotH;
    const slotCapTop = slotDimsBottom + slotGap;
    const slotCapBottom = slotCapTop + captionSlotH;
    const centerX = (brandLeftX + brandRightX) / 2;
    const nameBaselineY = (slotNameTop + slotNameBottom) / 2 + nameSize / 3;
    const dimsCenterY = (slotDimsTop + slotDimsBottom) / 2;
    const captionBaselineY = (slotCapTop + slotCapBottom) / 2 + captionSize / 3;
    const rowLeft = centerX - rowW / 2;
    const numLeft = rowLeft;
    const pillLeft = numLeft + numW + interGap;

    try {
      const nameText = new this.scope.PointText({
        point: [centerX, nameBaselineY],
        content: "drawleather.com",
        fillColor: new this.scope.Color(MAT_GRID_AXIS()),
        fontFamily: "sans-serif",
        fontSize: nameSize,
        fontWeight: 700,
        justification: "center",
      });
      nameText.data = { ephemeral: true };
      group.addChild(nameText);
    } catch {
      /* ignore */
    }

    // Number text (left side of the dimensions row, bold, full pill
    // outer height).
    try {
      const numText = new this.scope.PointText({
        point: [numLeft, dimsCenterY + numberSize / 3],
        content: numberText,
        fillColor: new this.scope.Color(MAT_GRID_AXIS()),
        fontFamily: "sans-serif",
        fontSize: numberSize,
        fontWeight: 700,
        justification: "left",
      });
      numText.data = { ephemeral: true };
      group.addChild(numText);
    } catch {
      /* ignore */
    }

    // Pill background — capsule shape (corner radius = half the height)
    // sitting to the right of the number text.
    try {
      const pillRect = new this.scope.Rectangle(
        new this.scope.Point(pillLeft, dimsCenterY - pillH / 2),
        new this.scope.Size(pillW, pillH),
      );
      const pillShape = new this.scope.Path.Rectangle(
        pillRect,
        new this.scope.Size(pillH / 2, pillH / 2),
      );
      pillShape.fillColor = new this.scope.Color(MAT_GRID_AXIS());
      pillShape.strokeColor = null;
      pillShape.data = { ephemeral: true };
      group.addChild(pillShape);
    } catch {
      /* ignore */
    }
    try {
      const pillText = new this.scope.PointText({
        point: [pillLeft + pillW / 2, dimsCenterY + pillTextSize / 3],
        content: unitText,
        fillColor: new this.scope.Color(MAT_GREEN()),
        fontFamily: "sans-serif",
        fontSize: pillTextSize,
        fontWeight: 700,
        justification: "center",
      });
      pillText.data = { ephemeral: true };
      group.addChild(pillText);
    } catch {
      /* ignore */
    }

    // Caption — "cutting mat" subtitle under the dimensions row.
    try {
      const capText = new this.scope.PointText({
        point: [centerX, captionBaselineY],
        content: captionText,
        fillColor: new this.scope.Color(MAT_GRID_AXIS()),
        fontFamily: "sans-serif",
        fontSize: captionSize,
        fontWeight: 400,
        justification: "center",
      });
      capText.data = { ephemeral: true };
      group.addChild(capText);
    } catch {
      /* ignore */
    }
  }

  /** Renders the text off-screen and returns its bounds width — used to
   *  pick a font size that fits inside a target width. Paper.js PointText
   *  width scales linearly with fontSize, so probing at size 10 and
   *  scaling is exact within rounding. */
  private measureTextWidth(
    text: string,
    weight: number,
    probeSize: number,
  ): number {
    try {
      const t = new this.scope.PointText({
        point: [0, 0],
        content: text,
        fontFamily: "sans-serif",
        fontSize: probeSize,
        fontWeight: weight,
        insert: false,
      });
      return t.bounds.width;
    } catch {
      return 0;
    }
  }

  /** Returns the ISO A-series name (A0…A5) for a mat's outer
   *  dimensions, or null when the mat is a custom size (oversized parts
   *  that exceed the largest standard). Both portrait and landscape
   *  orientations match. */
  private matchAseriesName(outerW: number, outerH: number): string | null {
    const eps = 1; // mm tolerance — standard sizes are exact, so this is
    // really only here to guard against floating-point drift.
    for (const s of STANDARD_MAT_SIZES_MM_METRIC) {
      if (
        (Math.abs(s.w - outerW) < eps && Math.abs(s.h - outerH) < eps) ||
        (Math.abs(s.h - outerW) < eps && Math.abs(s.w - outerH) < eps)
      ) {
        return s.name;
      }
    }
    return null;
  }

  /** Compact inch label — drops trailing zeros so 12 prints as "12" but
   *  18.5 still reads as "18.5". */
  private formatInches(mm: number): string {
    const inches = mm / 25.4;
    const rounded = Math.round(inches * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }

  /** Largest landscape standard mat (from the given size list) whose
   *  outer dimensions fit inside the canvas at 1:1 zoom. The canvas's
   *  CSS pixel size is translated to millimetres via the 96 px/inch CSS
   *  convention. Falls back to the smallest size when the canvas can't
   *  hold even that — keeps the empty default sensible while a tiny
   *  viewport is laying out. */
  private pickLargestMatFittingCanvas(
    sizes: ReadonlyArray<{ w: number; h: number }>,
  ): { w: number; h: number } {
    const viewSize = this.scope.view.viewSize;
    const pxPerMm = getPxPerMm();
    const availW = viewSize.width / pxPerMm;
    const availH = viewSize.height / pxPerMm;
    if (availW > 0 && availH > 0) {
      for (let i = sizes.length - 1; i >= 0; i--) {
        const lW = sizes[i]!.h; // landscape: long side becomes width
        const lH = sizes[i]!.w;
        if (lW <= availW && lH <= availH) {
          return { w: lW, h: lH };
        }
      }
    }
    const smallest = sizes[0]!;
    return { w: smallest.h, h: smallest.w };
  }
}
